import { AIR_DENSITY, GRAVITY, SURFACES } from './raceConditions';

/**
 * A catalogue record as a physical object (SPEC-008 PR-8.2).
 *
 * Everything the integrator needs and nothing it does not: a mass, a set of gear
 * ratios, an engine torque envelope, a drag area, and how much of the weight
 * sits on the driven axle. The two factors that make all of it agree with the
 * published figures are ARGUMENTS here; finding them is `carCalibration.js`.
 *
 * THE MODEL IS CALIBRATED TO THE CATALOGUE AND NOT THE OTHER WAY ROUND. That is
 * the decision this pair of files is arranged around. A simulation that quietly
 * disagreed with the spec table two bands below it on the same screen would make
 * one of the two wrong, and the reader has no way of knowing which — so a car
 * whose record says 4.1 seconds to 100 km/h does 4.1 seconds here, and a car
 * whose record says 324 km/h runs to 324 km/h here. What the model is FOR is the
 * conditions the catalogue does not publish, and it earns the right to speak
 * about those by reproducing the ones it does.
 *
 * Pure: numbers in, numbers out. No React, no browser, no `t()`, and no opinion
 * about how any of this is drawn.
 */

const WATTS_PER_HP = 745.699872;
const RADIANS_PER_RPM = Math.PI / 30;

/**
 * How much of the crankshaft's power reaches the road.
 *
 * All-wheel drive pays for its traction twice — a second differential and a
 * transfer case — which is a real part of why the 959 is not simply the fastest
 * car in this catalogue everywhere.
 */
const DRIVELINE_EFFICIENCY = { rwd: 0.88, fwd: 0.90, awd: 0.84 };

/**
 * How much of the weight sits over the back axle, by where the engine is.
 *
 * Read off the engine's POSITION rather than assumed per drivetrain, because it
 * is the single largest thing separating these cars at a standing start: a
 * rear-engined 911 has three fifths of itself over the driven wheels and a
 * front-engined Supra has slightly under half, on the same tyres, on the same
 * road. Modelled with one number for "rwd" the front-engined half of the
 * catalogue comes out about fifteen percent slow, and the calibration below
 * would then spend itself papering over a fact the record already carries.
 */
const REAR_WEIGHT_SHARE = { front: 0.50, mid: 0.58, rear: 0.61 };

/** Where the mass is, vertically, and how far apart the axles are. */
const CENTRE_OF_GRAVITY_HEIGHT = 0.42;
const WHEELBASE_SHARE_OF_LENGTH = 0.56;

/**
 * The gearbox, as a ratio between two gears and a count of the ones that matter.
 *
 * TOP SPEED IS REACHED IN THE HIGHEST WORKING GEAR, WHICH FOR A SIX-SPEED IS
 * FIFTH. Every six-speed in this catalogue has an overdrive top — a cruising
 * ratio the car does not see inside 400 metres and does not reach its maximum
 * in. Taking the numerically highest gear as the top-speed gear instead makes
 * first gear about a fifth too short, which puts two upshifts inside the run to
 * 100 km/h for cars that are remembered for reaching it in one.
 *
 * The consequence is worth saying plainly rather than leaving to be discovered:
 * GEAR COUNT BARELY MOVES A RESULT HERE. What decides a standing start is the
 * four ratios below the top-speed gear, and this catalogue's five- and
 * six-speeds space those almost identically — a real Viper's first-to-fifth
 * spread is 3.59 and a real F40's is 3.64. The count is still read rather than
 * assumed, so a four-speed record would be modelled as a four-speed.
 */
const GEAR_STEP = 1.36;
const WORKING_GEARS = 5;
const OVERDRIVE_STEP = 1.22;

/**
 * How far past peak power the engine will pull before the driver has to shift.
 *
 * A fixed multiple of the power peak is wrong at both ends of this catalogue: a
 * Ferrari V12 peaking at 7,000 rpm runs to about 7,750, and a Viper V10 peaking
 * at 4,600 runs to 6,000 — the same 900-odd rpm of overrun, not the same
 * fraction. The floor is there so a hypothetical very low-revving record does
 * not end up with a redline below its own power peak.
 */
const REDLINE_OVERRUN_RPM = 900;
const REDLINE_MINIMUM_RATIO = 1.06;

/** Where the driver holds it before dropping the clutch, and how long a shift costs. */
const LAUNCH_RPM_SHARE = 0.45;
export const SHIFT_SECONDS = 0.30;

/**
 * What the rotating parts add to the mass, as an equivalent-mass factor.
 *
 * NOT one number. The wheels are a fixed cost; the engine and clutch are
 * multiplied by the square of the gear ratio, so first gear costs a light car
 * with a short first about a fifth of its acceleration and a heavy car with a
 * tall one about a ninth. That difference is precisely the front-engined cars
 * this model was otherwise too slow for, and a single 1.06 factor is a top-gear
 * number being applied to a standing start.
 *
 * The engine's own inertia is scaled off displacement because that is the figure
 * the catalogue carries that tracks it: a 8.0-litre V10's crank and flywheel are
 * not a 1.3-litre rotary's.
 */
const WHEEL_INERTIA_SHARE = 1.04;
const ENGINE_INERTIA_BASE = 0.10;
const ENGINE_INERTIA_PER_CC = 5e-5;

/**
 * What fraction of peak torque the engine makes at rest, by how it breathes.
 *
 * The bottom of the curve cannot be fitted from the two published points — a
 * single parabola through peak torque and peak power collapses whenever those
 * two rpm figures sit close together, and it has the 512 TR making 42% of its
 * torque at launch, which is not a Ferrari flat-12. So the low end is read off
 * the `aspiration` vocabulary instead, which is a fact the record already
 * carries and which is exactly what decides the shape down there: a big period
 * turbo really does have very little below boost, and a supercharger really does
 * have it from idle.
 */
const LOW_END_TORQUE_SHARE = {
    natural: 0.72,
    turbo: 0.50,
    'twin-turbo': 0.55,
    supercharged: 0.78,
};

/**
 * Where the engine sits, from whichever field the fetched catalogue carries.
 *
 * `engine.position` is the closed vocabulary `carSchema.js` names, what the
 * authored content in `scripts/content/carSpecs.js` writes, and what the
 * committed catalogue now carries on all twenty-four records. It did not always:
 * the field was split out of a combined `engine.layout` — "mid-v8" — whose
 * first segment was the same vocabulary. The prefix parse below is kept for a
 * catalogue fetched from a cache that predates the split, since the app fetches
 * its content and can be handed an old document; it falls back to the most
 * common case rather than throwing.
 *
 * It is a boundary, and boundaries here parse rather than trust: the value is
 * matched against the table below and an unrecognised one takes the fallback,
 * so a content pack cannot reach the arithmetic with a string this file has
 * never heard of.
 */
function enginePosition(specs) {
    const stated = specs?.engine?.position;
    const parsed = String(specs?.engine?.layout || '').split('-')[0];
    const candidate = stated || parsed;

    return Object.prototype.hasOwnProperty.call(REAR_WEIGHT_SHARE, candidate) ? candidate : 'front';
}

/** Does this record carry everything the physics needs? */
export function canRace(car) {
    const specs = car?.specs;

    return Boolean(
        specs
        && specs.power?.hp > 0 && specs.power?.rpm > 0
        && specs.torque?.nm > 0 && specs.torque?.rpm > 0
        && specs.topSpeed?.kmh > 0
        && specs.zeroToHundred?.seconds > 0
        && specs.weightKg > 0
        && specs.lengthMm > 0
        && DRIVELINE_EFFICIENCY[specs.drivetrain],
    );
}

/**
 * The engine's torque envelope, as a function of crankshaft speed.
 *
 * Piecewise, and the seam is the torque peak: a straight rise to it from the
 * low-end share above, and a parabola above it that passes exactly through the
 * power peak. The parabola is what makes the top of the curve a consequence of
 * the published power figure rather than a guess — at peak power rpm the engine
 * makes, by definition, power over speed — and the straight line below is what
 * stops that same parabola from being extrapolated into a region it was never
 * fitted for.
 */
function torqueEnvelope(specs, tune) {
    const peakTorque = specs.torque.nm;
    const torqueOmega = specs.torque.rpm * RADIANS_PER_RPM;
    const powerOmega = specs.power.rpm * RADIANS_PER_RPM;
    const torqueAtPower = (specs.power.hp * WATTS_PER_HP) / powerOmega;
    const curvature = (torqueAtPower - peakTorque) / ((powerOmega - torqueOmega) ** 2);
    const lowEnd = LOW_END_TORQUE_SHARE[specs.engine?.aspiration] ?? LOW_END_TORQUE_SHARE.natural;

    return (omega) => {
        const shape = omega < torqueOmega
            ? lowEnd + (1 - lowEnd) * (omega / torqueOmega)
            : 1 + (curvature * (omega - torqueOmega) ** 2) / peakTorque;

        return Math.max(0, peakTorque * shape * tune);
    };
}

/**
 * The gear ratios, as crank radians per second per metre per second of road
 * speed.
 *
 * Expressed that way rather than as gearbox ratios and a wheel radius because
 * the wheel radius is not in the catalogue and cancels out of every equation
 * that needs it: torque times this number is a force at the road, and this
 * number times road speed is a crank speed. Two facts, no third unknown.
 */
function gearRatios(specs) {
    const powerOmega = specs.power.rpm * RADIANS_PER_RPM;
    const topSpeed = specs.topSpeed.kmh / 3.6;
    // Top speed is peak power in the highest WORKING gear, which is what makes
    // the drag derivation below and this share one consistent statement about
    // the same moment.
    const working = Math.min(specs.transmission?.gears || WORKING_GEARS, WORKING_GEARS);
    const workingTop = powerOmega / topSpeed;
    const ratios = [];

    for (let gear = 1; gear <= working; gear += 1) {
        ratios.push(workingTop * GEAR_STEP ** (working - gear));
    }

    for (let gear = working; gear < (specs.transmission?.gears || working); gear += 1) {
        ratios.push(ratios[ratios.length - 1] / OVERDRIVE_STEP);
    }

    return ratios;
}

/**
 * Frontal drag area, derived from the car's own published top speed (D-4).
 *
 * At top speed the car is not accelerating, so every watt reaching the road is
 * spent on aerodynamic drag and rolling resistance. Rearranged, that gives CdA
 * from two figures the catalogue already carries — and it is the decision that
 * makes this model honest rather than merely plausible, because it means a car
 * the catalogue says runs to 324 km/h runs to 324 km/h here by construction.
 *
 * The numbers it produces are the check that the derivation is sound and not
 * just self-consistent: 0.68 m² for the F40 and 1.13 for the Viper are within a
 * few percent of the published wind-tunnel figures for both, and neither of
 * those was an input.
 *
 * Floored rather than allowed to go negative, because a content pack claiming a
 * top speed its power cannot reach would otherwise produce a car that drag
 * ACCELERATES.
 */
function dragArea(specs, tune) {
    const topSpeed = specs.topSpeed.kmh / 3.6;
    const wheelWatts = specs.power.hp * WATTS_PER_HP * DRIVELINE_EFFICIENCY[specs.drivetrain] * tune;
    const rolling = SURFACES.dry.rolling * specs.weightKg * GRAVITY;

    return Math.max(0.2, (2 * (wheelWatts / topSpeed - rolling)) / (AIR_DENSITY * topSpeed ** 2));
}

/**
 * The record as a physical object, at a stated grip and tune.
 *
 * Both factors are arguments rather than something this function decides,
 * because deciding them means running the model — and a module that searched
 * over its own output would be two responsibilities in one file.
 * `carCalibration.js` is the search; this is what it searches over.
 */
export function carDynamics(car, { grip = 1, tune = 1 } = {}) {
    const specs = car.specs;
    const ratios = gearRatios(specs);
    const rearShare = REAR_WEIGHT_SHARE[enginePosition(specs)];
    const drivenShare = specs.drivetrain === 'awd'
        ? 1
        : (specs.drivetrain === 'rwd' ? rearShare : 1 - rearShare);

    return {
        mass: specs.weightKg,
        ratios,
        torqueAt: torqueEnvelope(specs, tune),
        efficiency: DRIVELINE_EFFICIENCY[specs.drivetrain],
        dragArea: dragArea(specs, tune),
        redline: Math.max(
            specs.power.rpm * REDLINE_MINIMUM_RATIO,
            specs.power.rpm + REDLINE_OVERRUN_RPM,
        ) * RADIANS_PER_RPM,
        launchOmega: specs.power.rpm * RADIANS_PER_RPM * LAUNCH_RPM_SHARE,
        drivenShare,
        // Weight transfer loads the driven axle on a rear-drive car and unloads
        // it on a front-drive one. All-wheel drive already has all of it and
        // transfer only moves it between two axles that are both driven.
        transferSign: specs.drivetrain === 'rwd' ? 1 : (specs.drivetrain === 'fwd' ? -1 : 0),
        transferRatio: CENTRE_OF_GRAVITY_HEIGHT / ((specs.lengthMm / 1000) * WHEELBASE_SHARE_OF_LENGTH),
        engineInertia: ENGINE_INERTIA_BASE + ENGINE_INERTIA_PER_CC * (specs.engine?.displacementCc || 0),
        grip,
        tune,
        topSpeedKmh: specs.topSpeed.kmh,
        publishedZeroToHundred: specs.zeroToHundred.seconds,
    };
}

/**
 * The equivalent-mass factor in a given gear — what the rotating parts add.
 *
 * Exported because the integrator is the only caller and the arithmetic belongs
 * with the constants that feed it rather than in the loop that uses it.
 */
export function inertiaFactor(dynamics, gear) {
    return WHEEL_INERTIA_SHARE + (dynamics.engineInertia * dynamics.ratios[gear] ** 2) / dynamics.mass;
}
